import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Hook para gestionar las notificaciones push
 */
export function usePushNotifications(user) {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar soporte del navegador
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported && 'Notification' in window) {
      setPermission(Notification.permission);
    }
    
    setLoading(false);
  }, []);

  // Verificar estado de suscripción al cargar
  useEffect(() => {
    if (user?.id && isSupported) {
      checkSubscriptionStatus();
    }
  }, [user?.id, isSupported]);

  const checkSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/push/status/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSubscribed(response.data.subscribed);
    } catch (err) {
      console.error('Error verificando estado de suscripción:', err);
    }
  };

  // Registrar Service Worker
  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Workers no soportados');
    }

    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
    return registration;
  };

  // Convertir base64 a Uint8Array (para la clave VAPID)
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Suscribirse a notificaciones push
  const subscribe = useCallback(async () => {
    if (!user?.id) {
      setError('Usuario no autenticado');
      return false;
    }

    if (!user?.is_admin) {
      setError('Solo administradores pueden suscribirse');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Solicitar permiso
      const notificationPermission = await Notification.requestPermission();
      setPermission(notificationPermission);

      if (notificationPermission !== 'granted') {
        setError('Permiso de notificaciones denegado');
        setLoading(false);
        return false;
      }

      // Registrar Service Worker
      const registration = await registerServiceWorker();

      // Obtener clave pública VAPID del backend
      const token = localStorage.getItem('token');
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const vapidPublicKey = vapidResponse.data.public_key;

      if (!vapidPublicKey) {
        throw new Error('No se pudo obtener la clave VAPID');
      }

      // Suscribirse a push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Enviar suscripción al backend
      const subscriptionData = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionData.endpoint,
        keys: subscriptionData.keys,
        user_id: user.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsSubscribed(true);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Error al suscribirse:', err);
      setError(err.message || 'Error al suscribirse a notificaciones');
      setLoading(false);
      return false;
    }
  }, [user]);

  // Cancelar suscripción
  const unsubscribe = useCallback(async () => {
    if (!user?.id) return false;

    setLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Eliminar del backend
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/push/unsubscribe`, {
          params: { user_id: user.id, endpoint: subscription.endpoint },
          headers: { Authorization: `Bearer ${token}` }
        });

        // Cancelar suscripción local
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      setLoading(false);
      return true;

    } catch (err) {
      console.error('Error al cancelar suscripción:', err);
      setError(err.message || 'Error al cancelar suscripción');
      setLoading(false);
      return false;
    }
  }, [user]);

  // Enviar notificación de prueba
  const sendTestNotification = useCallback(async () => {
    if (!user?.id) return false;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/push/test?user_id=${user.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (err) {
      console.error('Error enviando notificación de prueba:', err);
      throw err;
    }
  }, [user]);

  return {
    isSupported,
    permission,
    isSubscribed,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTestNotification,
    checkSubscriptionStatus
  };
}

export default usePushNotifications;
